/**
 * A snap's caption and emojis, as the server will keep them.
 *
 * Whatever the phone sends is drawn on everybody else's screen, so it is
 * checked once, here, by createUpload before the upload is recorded. Numbers
 * are brought into range rather than refused -- a sticker twisted three times
 * round is still a sticker, and refusing it would lose the whole snap -- but
 * anything that is not the right shape at all is refused.
 */
import { captionStyleOf } from './captionStyle';
import { SNAP_CAPTION_MAX, SNAP_STICKERS_MAX } from './constants';
import { isSnapFilter } from './snapFilters';
import { isStickerArt } from './stickerArt';
import type { CaptionAt, SnapDecor, SnapSticker } from './types';

/**
 * The frame words and emojis may be placed in: a tall phone's shape, about 9
 * by 19.5, round the picture. On a phone it is the whole screen, the blurred
 * fill above and below a picture included; the viewer makes room for all of
 * it wherever it is shown, so something put in the fill is never cut off.
 */
export const SNAP_FRAME_RATIO = 9 / 19.5;

/** How far past the picture's edges a placement may go, in fractions of it. */
export interface Reach {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export const PHOTO_REACH: Reach = { x0: 0, x1: 1, y0: 0, y1: 1 };

/**
 * The frame round a picture of this shape (width over height): a picture
 * wider than the frame has room above and below it; one as tall or taller
 * has none.
 */
export function reachOf(ratio: number): Reach {
  if (!(ratio > SNAP_FRAME_RATIO)) return PHOTO_REACH;
  const extra = (ratio / SNAP_FRAME_RATIO - 1) / 2;
  return { x0: 0, x1: 1, y0: -extra, y1: 1 + extra };
}

/** Whether anything is placed off the picture, in the fill round it. */
export function reachesPast(decor: SnapDecor): boolean {
  const off = (at: CaptionAt | undefined) => !!at && (at.x < 0 || at.x > 1 || at.y < 0 || at.y > 1);
  return off(decor.caption ? decor.captionAt : undefined) || (decor.stickers ?? []).some((s) => off(s.at));
}

/** Past these a caption or a sticker stops being something on the picture. */
export const DECOR_MIN_SCALE = 0.25;
export const DECOR_MAX_SCALE = 4;
/** One emoji, which may be several code points joined; never a sentence. */
const EMOJI_MAX_CHARS = 16;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** Degrees folded into (-180, 180], which is every turn there is. */
export function foldTurn(degrees: number): number {
  const folded = ((((degrees + 180) % 360) + 360) % 360) - 180;
  return folded === -180 ? 180 : folded;
}

/**
 * No letters and no spaces, which is what keeps a sticker from being a second
 * caption. A digit only as a keycap, 1️⃣, which is a digit joined to U+20E3.
 */
function looksLikeEmoji(glyph: string): boolean {
  if (/[\p{L}\s]/u.test(glyph)) return false;
  if (/\p{Nd}/u.test(glyph) && !glyph.includes('⃣')) return false;
  return true;
}

/**
 * How narrow and how wide a caption's box may be dragged, as fractions of the
 * frame: a word or two a line at the narrowest, the frame across at most.
 */
export const CAPTION_MIN_WIDTH = 0.15;
export const CAPTION_MAX_WIDTH = 1;

function placeOf(raw: unknown): CaptionAt | null {
  if (!raw || typeof raw !== 'object') return null;
  const { x, y, rotate, scale, width } = raw as Record<string, unknown>;
  const numbers = [x, y, rotate, scale];
  if (!numbers.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  if (width !== undefined && !(typeof width === 'number' && Number.isFinite(width))) return null;
  // Off the picture is allowed, into the frame round it (reachOf); a picture
  // as wide as a sheet of paper still has under a picture's height either side.
  return {
    x: clamp(x as number, -1, 2),
    y: clamp(y as number, -1, 2),
    rotate: foldTurn(rotate as number),
    scale: clamp(scale as number, DECOR_MIN_SCALE, DECOR_MAX_SCALE),
    // The width a caption's edge was dragged to. Dropped here, it would come
    // back as wide as its words on everybody else's screen.
    ...(width !== undefined ? { width: clamp(width as number, CAPTION_MIN_WIDTH, CAPTION_MAX_WIDTH) } : {}),
  };
}

/**
 * Rounded before it is stored. A drag produces a number with seventeen digits
 * after the point, none of which anybody can see.
 */
function rounded(at: CaptionAt): CaptionAt {
  const round = (value: number) => Math.round(value * 1000) / 1000;
  return {
    x: round(at.x),
    y: round(at.y),
    rotate: round(at.rotate),
    scale: round(at.scale),
    ...(at.width !== undefined ? { width: round(at.width) } : {}),
  };
}

/**
 * What a phone writes onto its own GiviSnap: the words cut to length, every
 * place rounded, and no more emojis than rules allow. A placement goes only
 * with words to place, and nothing is written that was not there to write.
 */
export function storedDecor(decor: SnapDecor): SnapDecor {
  return {
    ...(decor.caption ? { caption: decor.caption.slice(0, SNAP_CAPTION_MAX) } : {}),
    ...(decor.caption && decor.captionAt ? { captionAt: rounded(decor.captionAt) } : {}),
    ...(decor.caption && captionStyleOf(decor.captionStyle)
      ? { captionStyle: captionStyleOf(decor.captionStyle) }
      : {}),
    ...(decor.stickers?.length
      ? {
          stickers: decor.stickers
            .slice(0, SNAP_STICKERS_MAX)
            .map((sticker) =>
              'art' in sticker
                ? { art: sticker.art, at: rounded(sticker.at) }
                : { emoji: sticker.emoji, at: rounded(sticker.at) },
            ),
        }
      : {}),
  };
}

/**
 * The decor to keep, `{}` for none, or null when it cannot be kept at all.
 *
 * An empty caption is no caption, and a caption's placement goes with it.
 */
export function snapDecorOf(raw: unknown): SnapDecor | null {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { caption, captionAt, captionStyle, stickers, filter, ...rest } = raw as Record<string, unknown>;
  if (Object.keys(rest).length > 0) return null;

  const decor: SnapDecor = {};

  // A look it does not know is dropped rather than refused: the snap is worth
  // more than its filter.
  if (isSnapFilter(filter)) decor.filter = filter;

  if (caption !== undefined) {
    if (typeof caption !== 'string') return null;
    const text = caption.trim();
    if (text.length > SNAP_CAPTION_MAX) return null;
    if (text) {
      const at = captionAt === undefined ? { x: 0.5, y: 0.5, rotate: 0, scale: 1 } : placeOf(captionAt);
      if (!at) return null;
      decor.caption = text;
      decor.captionAt = at;
      // A face or colour it does not know goes back to the default, as an
      // unknown filter is dropped: the words matter more than their look.
      const style = captionStyleOf(captionStyle);
      if (style) decor.captionStyle = style;
    }
  } else if (captionAt !== undefined) {
    return null;
  }

  if (stickers !== undefined) {
    if (!Array.isArray(stickers) || stickers.length > SNAP_STICKERS_MAX) return null;
    const kept: SnapSticker[] = [];
    for (const sticker of stickers) {
      if (!sticker || typeof sticker !== 'object') return null;
      const { emoji, art, at, ...extra } = sticker as Record<string, unknown>;
      if (Object.keys(extra).length > 0) return null;
      // A drawing, by its name on the list, and never an emoji as well.
      if (art !== undefined) {
        if (emoji !== undefined || !isStickerArt(art)) return null;
        const place = placeOf(at);
        if (!place) return null;
        kept.push({ art, at: place });
        continue;
      }
      if (typeof emoji !== 'string') return null;
      const glyph = emoji.trim();
      if (!glyph || glyph.length > EMOJI_MAX_CHARS || !looksLikeEmoji(glyph)) return null;
      const place = placeOf(at);
      if (!place) return null;
      kept.push({ emoji: glyph, at: place });
    }
    if (kept.length) decor.stickers = kept;
  }

  return decor;
}
